import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box, TextField, Button, Typography, Alert } from '@mui/material';
import { Search as SearchIcon, LocationOn as LocationIcon } from '@mui/icons-material';

// Accessible MVP Search Component
// Focus: Position search, Location search, Find Jobs button only
// WCAG 2.1 AA compliant with proper contrast ratios and keyboard navigation

const SearchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  maxWidth: '800px',
  margin: '0 auto',
  padding: '2rem',
  
  // Responsive design
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'flex-end'
  }
}));

const SearchField = styled(TextField)(({ theme }) => ({
  flex: 1,
  
  '& .MuiInputBase-root': {
    minHeight: '44px', // WCAG AA minimum touch target
    fontSize: '16px', // Prevents zoom on iOS
    backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : '#2d2d2d',
    borderRadius: '8px',
    
    '&:hover': {
      backgroundColor: theme.palette.mode === 'light' ? '#f8f9fa' : '#363636'
    },
    
    '&.Mui-focused': {
      backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : '#2d2d2d'
    }
  },
  
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.mode === 'light' ? '#cccccc' : '#555555',
    borderWidth: '2px'
  },
  
  '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.mode === 'light' ? '#999999' : '#777777'
  },
  
  '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#009639', // Jamaica green
    borderWidth: '2px'
  },
  
  '& .MuiInputBase-input': {
    color: theme.palette.mode === 'light' ? '#333333' : '#ffffff',
    padding: '12px 16px',
    
    '&::placeholder': {
      color: theme.palette.mode === 'light' ? '#666666' : '#cccccc',
      opacity: 1
    }
  },
  
  '& .MuiInputLabel-root': {
    color: theme.palette.mode === 'light' ? '#666666' : '#cccccc',
    
    '&.Mui-focused': {
      color: '#009639'
    }
  }
}));

const SearchButton = styled(Button)(({ theme }) => ({
  minHeight: '44px',
  minWidth: '120px',
  fontSize: '16px',
  fontWeight: 600,
  borderRadius: '8px',
  textTransform: 'none',
  backgroundColor: '#009639', // Jamaica green
  color: '#ffffff',
  border: '2px solid #009639',
  
  '&:hover': {
    backgroundColor: '#007a2d',
    borderColor: '#007a2d',
    transform: 'translateY(-1px)'
  },
  
  '&:focus': {
    outline: `3px solid ${theme.palette.mode === 'light' ? '#4d9fff' : '#66b3ff'}`,
    outlineOffset: '2px'
  },
  
  '&:disabled': {
    backgroundColor: theme.palette.mode === 'light' ? '#e0e0e0' : '#424242',
    color: theme.palette.mode === 'light' ? '#9e9e9e' : '#757575',
    borderColor: theme.palette.mode === 'light' ? '#e0e0e0' : '#424242'
  },
  
  // High contrast mode support
  '@media (prefers-contrast: high)': {
    border: '3px solid #000000',
    backgroundColor: '#009639',
    color: '#ffffff'
  }
}));

const SearchTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.mode === 'light' ? '#333333' : '#ffffff',
  marginBottom: '1rem',
  fontWeight: 600,
  textAlign: 'center',
  
  [theme.breakpoints.up('md')]: {
    textAlign: 'left'
  }
}));

const SimpleMVPSearch = ({ onSearch, showTitle = true }) => {
  const [position, setPosition] = useState('');
  const [location, setLocation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Input sanitization and validation
  const sanitizeInput = (input) => {
    if (!input || typeof input !== 'string') return '';
    
    // Remove potentially dangerous characters and limit length
    return input
      .replace(/[<>"'&]/g, '') // Remove HTML/script injection chars
      .replace(/[{}\[\]]/g, '') // Remove object notation
      .trim()
      .substring(0, 100); // Limit length
  };
  
  const validateInput = (input) => {
    if (!input || input.length < 2) {
      return 'Please enter at least 2 characters';
    }
    if (input.length > 100) {
      return 'Search query too long (max 100 characters)';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Sanitize inputs
    const cleanPosition = sanitizeInput(position);
    const cleanLocation = sanitizeInput(location);
    
    // Validate inputs
    const positionError = validateInput(cleanPosition);
    if (positionError) {
      setError(positionError);
      return;
    }
    
    setError('');
    setIsLoading(true);
    
    try {
      // Clean search parameters
      const searchParams = {
        q: cleanPosition,
        location: cleanLocation
      };
      
      // If parent component provides onSearch callback
      if (onSearch) {
        await onSearch(searchParams);
      } else {
        // Default navigation to search results
        const urlParams = new URLSearchParams();
        urlParams.set('q', searchParams.q);
        if (searchParams.location) {
          urlParams.set('location', searchParams.location);
        }
        navigate(`/jobs?${urlParams.toString()}`);
      }
    } catch (err) {
      setError('Search failed. Please try again.');
      console.error('Search error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePositionChange = (e) => {
    setPosition(e.target.value);
    if (error) setError(''); // Clear error when user starts typing
  };

  return (
    <Box role="search" aria-label="Job search">
      {showTitle && (
        <SearchTitle variant="h4" component="h1">
          Find Your Dream Job in Jamaica
        </SearchTitle>
      )}
      
      <form onSubmit={handleSubmit} noValidate>
        <SearchContainer>
          <SearchField
            label="Job Title or Keywords"
            placeholder="e.g., Software Developer, Marketing Manager, Teacher"
            value={position}
            onChange={handlePositionChange}
            required
            autoComplete="off"
            inputProps={{
              'aria-label': 'Enter job title or keywords',
              'aria-required': 'true'
            }}
            InputProps={{
              startAdornment: (
                <SearchIcon 
                  sx={{ 
                    color: 'action.active', 
                    mr: 1,
                    'aria-hidden': 'true' 
                  }} 
                />
              )
            }}
          />
          
          <SearchField
            label="Location (Optional)"
            placeholder="e.g., Kingston, Montego Bay, Spanish Town"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            autoComplete="off"
            inputProps={{
              'aria-label': 'Enter location in Jamaica (optional)'
            }}
            InputProps={{
              startAdornment: (
                <LocationIcon 
                  sx={{ 
                    color: 'action.active', 
                    mr: 1,
                    'aria-hidden': 'true' 
                  }} 
                />
              )
            }}
          />
          
          <SearchButton
            type="submit"
            variant="contained"
            disabled={isLoading || !position.trim()}
            aria-label={isLoading ? 'Searching for jobs...' : 'Search for jobs'}
          >
            {isLoading ? 'Searching...' : 'Find Jobs'}
          </SearchButton>
        </SearchContainer>
      </form>
      
      {error && (
        <Alert 
          severity="error" 
          sx={{ mt: 2, maxWidth: '800px', margin: '1rem auto 0' }}
          role="alert"
        >
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default SimpleMVPSearch;